import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";

// Janelas que flutuam por cima do sistema:
//  - calculadora de amortização (presa à conversa onde foi aberta);
//  - conversas minimizadas (bolinhas tipo Messenger, valem em todas as páginas).
const FlutuantesContext = createContext({});
// Só as funções (nunca mudam). Quem só ABRE algo usa este, e não é
// redesenhado a cada movimento da bolinha ou tecla na calculadora.
export const FlutuantesAcoesContext = createContext({});

const CHAVE = "ia_conversas_minimizadas";
const MAX_CONVERSAS = 5;

export const DADOS_CALC_INICIAIS = {
  valor: "",
  sistema: "price",
  parcelas: "12",
  taxa: "",
  tipoTaxa: "mensal",
  inicio: "",
};

const lerSalvas = () => {
  try {
    const v = JSON.parse(localStorage.getItem(CHAVE) || "[]");
    return Array.isArray(v) ? v.map((c) => ({ ...c, expandida: false })) : [];
  } catch (e) {
    return [];
  }
};

const posPadraoBolinha = (indice) => ({
  x: Math.max(8, window.innerWidth - 84),
  y: Math.max(8, window.innerHeight - 170 - indice * 72),
});

export const FlutuantesProvider = ({ children }) => {
  const [calc, setCalc] = useState(null);
  const [conversas, setConversas] = useState(lerSalvas);

  useEffect(() => {
    try {
      localStorage.setItem(
        CHAVE,
        JSON.stringify(conversas.map(({ id, nome, foto, pos }) => ({ id, nome, foto, pos })))
      );
    } catch (e) {
      /* navegador sem localStorage: segue só na memória */
    }
  }, [conversas]);

  // ── Calculadora ──
  const abrirCalculadora = useCallback((ticketId) => {
    setCalc((c) =>
      c && c.ticketId === String(ticketId)
        ? { ...c, minimizada: false }
        : {
            ticketId: String(ticketId),
            minimizada: false,
            dados: { ...DADOS_CALC_INICIAIS },
            pos: null,
            posBolinha: null,
          }
    );
  }, []);
  const minimizarCalculadora = useCallback(() => setCalc((c) => (c ? { ...c, minimizada: true } : c)), []);
  const restaurarCalculadora = useCallback(() => setCalc((c) => (c ? { ...c, minimizada: false } : c)), []);
  const fecharCalculadora = useCallback(() => setCalc(null), []);
  const atualizarCalculadora = useCallback(
    (parcial) => setCalc((c) => (c ? { ...c, ...parcial } : c)),
    []
  );

  // ── Conversas minimizadas ──
  const minimizarConversa = useCallback(({ id, nome, foto }) => {
    if (!id) return;
    setConversas((lista) => {
      const semEla = lista.filter((c) => String(c.id) !== String(id));
      const nova = { id: String(id), nome: nome || "Cliente", foto: foto || "", pos: null, expandida: false };
      const resultado = [...semEla, nova].slice(-MAX_CONVERSAS);
      return resultado.map((c, i) => (c.pos ? c : { ...c, pos: posPadraoBolinha(i) }));
    });
  }, []);

  const atualizarConversa = useCallback((id, parcial) => {
    setConversas((lista) =>
      lista.map((c) => (String(c.id) === String(id) ? { ...c, ...parcial } : c))
    );
  }, []);

  // Abre uma e recolhe as outras (uma janelinha de cada vez).
  const expandirConversa = useCallback((id, expandir = true) => {
    setConversas((lista) =>
      lista.map((c) =>
        String(c.id) === String(id)
          ? { ...c, expandida: expandir }
          : expandir
          ? { ...c, expandida: false }
          : c
      )
    );
  }, []);

  const fecharConversa = useCallback((id) => {
    setConversas((lista) => lista.filter((c) => String(c.id) !== String(id)));
  }, []);

  const acoes = useMemo(
    () => ({
      abrirCalculadora,
      minimizarCalculadora,
      restaurarCalculadora,
      fecharCalculadora,
      atualizarCalculadora,
      minimizarConversa,
      atualizarConversa,
      expandirConversa,
      fecharConversa,
    }),
    [
      abrirCalculadora,
      minimizarCalculadora,
      restaurarCalculadora,
      fecharCalculadora,
      atualizarCalculadora,
      minimizarConversa,
      atualizarConversa,
      expandirConversa,
      fecharConversa,
    ]
  );
  const tudo = useMemo(() => ({ calc, conversas, ...acoes }), [calc, conversas, acoes]);

  return (
    <FlutuantesAcoesContext.Provider value={acoes}>
      <FlutuantesContext.Provider value={tudo}>{children}</FlutuantesContext.Provider>
    </FlutuantesAcoesContext.Provider>
  );
};

export default FlutuantesContext;
